import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import './TrcProbeDetails.scss';
import NavigationBar from '../../../navigation/NavigationBar';
import ReadingRecord from './readingRecord/ReadingRecord';
import Comprehension from './comprehension/Comprehension';
import LanguageWritingGrammarSpelling from './languageWritingGrammarSpelling/LanguageWritingGrammarSpelling';
import BookNavigation from './BookNavigation';
import SpecialBookTypeView from './specialBookType/SpecialBookTypeView';
import SideBar from './sideBar/SideBar';
import TrcStatusSection from './sideBar/TrcStatusSection';
import { getUrlParams } from '../../../../utils/shared/urlUtils';
import { getBookItem, findBookResult } from '../../../../utils/shared/trcUtils';
import Spinner from '../../../../components/spinner/Spinner';
import { RESULT_TYPE, TRC_LEGEND_CONTENT, ID } from '../../../../constants/shared/TrcConstants';
import getTrcNavTabs from './trcNavTabs';

const { READING_RECORD: READING_RECORD_TYPE } = RESULT_TYPE;
const { READING_RECORD, COMPREHENSION, LANGUAGE_WRITING_GRAMMAR_SPELLING, SPECIAL } = ID;

const getSelectedTabId = (resultType, currentTab) =>
    resultType === READING_RECORD_TYPE ? currentTab || READING_RECORD : SPECIAL;

const getPageContent = (selectedTabId, pageContentProps) => {
    const {
        comprehensionProps,
        specialBookTypeViewProps,
        languageWritingGrammarSpellingProps,
        readingRecordProps,
    } = pageContentProps;

    switch (selectedTabId) {
        case COMPREHENSION:
            return <Comprehension {...comprehensionProps} />;
        case SPECIAL:
            return <SpecialBookTypeView {...specialBookTypeViewProps} />;
        case LANGUAGE_WRITING_GRAMMAR_SPELLING:
            return <LanguageWritingGrammarSpelling {...languageWritingGrammarSpellingProps} />;
        default:
            return <ReadingRecord {...readingRecordProps} />;
    }
};

const TrcProbeDetails = ({
    result,
    location,
    student,
    language,
    withNotes,
    themeClassName,
    baseUrl,
    values,
    selectBook,
    fetchItemLevelDetails,
    LinkWrapper,
}) => {
    useEffect(() => {
        if (isEmpty(result.itemLevelDetails)) fetchItemLevelDetails(result);
    }, []);

    const { itemLevelDetails, resultProbeUid } = result;

    if (isEmpty(itemLevelDetails)) return <Spinner />;

    const { tab, bookId } = getUrlParams(location);
    const bookResult = findBookResult(itemLevelDetails, bookId, resultProbeUid);

    if (isEmpty(bookResult)) return <Spinner />;

    const { probeDetailResult, trcScore } = bookResult;
    const { resultType, kopItems, pages, textNoteList, rubrics } = probeDetailResult;
    const selectedTabId = getSelectedTabId(resultType, tab);
    const isSpecialBookResultSelected = selectedTabId === SPECIAL;
    const pageContentProps = {
        comprehensionProps: { values: values.comprehensionValues, student, result: bookResult },
        specialBookTypeViewProps: { kopItems },
        languageWritingGrammarSpellingProps: { rubrics, language },
        readingRecordProps: { paragraphs: pages, baseUrl },
    };
    const pageContent = getPageContent(selectedTabId, pageContentProps);

    return (
        <>
            <div className="nav-holder">
                <div id="demo-nav-links">
                    {!isSpecialBookResultSelected && (
                        <NavigationBar
                            tabs={getTrcNavTabs(getUrlParams(location), !isEmpty(rubrics))}
                            selectedTabId={selectedTabId}
                            ulTagClassName="nav nav-horizontal"
                            LinkWrapper={LinkWrapper}
                            navTabsDataTestId="trc-nav-tabs"
                        />
                    )}
                </div>
            </div>
            <div className={`content ${themeClassName}`}>
                <div className="top-padding">
                    <BookNavigation
                        className="trc-align"
                        books={itemLevelDetails.map(book => getBookItem(book.trcScore))}
                        selectedBook={getBookItem(trcScore)}
                        selectBook={selectBook}
                        location={location}
                    />
                    <div className="content-wrapper">
                        {pageContent}
                        <SideBar
                            notes={withNotes ? textNoteList : null}
                            legend={selectedTabId === READING_RECORD ? TRC_LEGEND_CONTENT : null}
                            statusSection={
                                isSpecialBookResultSelected ? null : (
                                    <TrcStatusSection
                                        result={bookResult}
                                        showMsvGraph={itemLevelDetails.length > 1}
                                    />
                                )
                            }
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

TrcProbeDetails.defaultProps = {
    result: {},
    student: {},
    values: {},
    language: null,
    withNotes: true,
    themeClassName: '',
    baseUrl: null,
    location: {},
    fetchItemLevelDetails: () => {},
    selectBook: () => {},
    LinkWrapper: () => {},
};

TrcProbeDetails.propTypes = {
    result: PropTypes.object,
    student: PropTypes.object,
    values: PropTypes.object,
    language: PropTypes.string,
    withNotes: PropTypes.bool,
    themeClassName: PropTypes.string,
    baseUrl: PropTypes.string,
    location: PropTypes.object,
    fetchItemLevelDetails: PropTypes.func,
    selectBook: PropTypes.func,
    LinkWrapper: PropTypes.func,
};

export default TrcProbeDetails;
